import * as wjcCore from '@mescius/wijmo';
import * as wjcGrid from '@mescius/wijmo.grid';
import * as wjcGridXlsx from '@mescius/wijmo.grid.xlsx';
import {Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {DataService, OrientDBCollectionView} from '@cosbeni/database';
import {ActivePromotionConfig} from '../config/active-promotion.config';
import {KobeacService} from './kobeac.service';
import {TranslocoService} from '@jsverse/transloco';

interface ColumnDef {
    header: string;
    /** 表示見出しの翻訳キー (kobeac.<key>)。header/binding は日本語フィールド名のまま。 */
    key: string;
    binding: string;
    dataType?: string;
    format?: string;
    width?: number;
    dataMap?: 'auction' | 'supplier' | 'bidder' | 'category' | 'report' | 'payment';
}

/** kobe-ac マスター管理 (旧 /admin/master、7 種の汎用グリッド CRUD) */
@Component({
    standalone: false,
    selector: 'kobeac-master',
    templateUrl: 'master.component.html',
})
export class KobeacMasterComponent implements OnInit {
    @ViewChild('grid') grid: wjcGrid.FlexGrid;

    /** マスター管理メニューから外す種別。顧客/オークションは専用メニュー(admin/customer,
     *  admin/auction)へ分離。アイテムは「アイテム管理」画面、レポートはレポート designer で扱う。 */
    /** マスタ種別ごとの既定の並び順 (サーバ側 ORDER BY)。オークションは最新の日付を先頭にする。 */
    private static readonly DEFAULT_SORT: { [name: string]: { binding: string; ascending: boolean } } = {
        'オークション': {binding: '日付', ascending: false},
    };
    private static readonly HIDDEN_IN_MASTER = ['顧客', 'オークション', 'アイテム', 'レポート'];

    /** route data.master で単一マスタに固定された種別 (専用メニュー時)。無ければ null。 */
    fixedMaster: string | null = null;
    /** メニュー: 表示名 (単一固定時はその1件、通常は分離分を除いた種別) */
    menus: string[] = [];
    shortname = '顧客';
    className = 'AC_Customer';
    columns: ColumnDef[] = [];
    cv: OrientDBCollectionView;
    readonly = true;

    auctionMap: wjcGrid.DataMap;
    supplierMap: wjcGrid.DataMap;
    bidderMap: wjcGrid.DataMap;
    categoryMap: wjcGrid.DataMap;
    /** レポート名 (AC_Report.name) の文字列→文字列 DataMap。オークションのテンプレ選択用。 */
    reportMap: wjcGrid.DataMap;
    /** 支払方法 (AC_Master category='支払方法') の文字列→文字列 DataMap。顧客の支払方法列用。 */
    paymentMap: wjcGrid.DataMap;

    /** マスタ種別ごとの列定義 (旧 views/admin/master/*.html 準拠) */
    private readonly COLUMNS: { [name: string]: ColumnDef[] } = {
        '顧客': [
            {header: 'ID', key: 'id', binding: 'ID', dataType: 'String', width: 100},
            {header: '名称', key: 'name', binding: '名称', dataType: 'String', width: 200},
            {header: '売主', key: 'seller', binding: '売主', dataType: 'Boolean', width: 100},
            {header: '買主', key: 'buyer', binding: '買主', dataType: 'Boolean', width: 100},
            {header: '参加費不要', key: 'no_entry_fee', binding: '参加費不要', dataType: 'Boolean', width: 100},
            {header: '無効', key: 'inactive', binding: '無効', dataType: 'Boolean', width: 100},
            {header: 'インボイス登録', key: 'invoice_registered', binding: 'インボイス登録', dataType: 'Boolean', width: 120},
            {header: '登録番号', key: 'registration_no', binding: '登録番号', dataType: 'String', width: 160},
            {header: '支払方法', key: 'payment_method', binding: '支払方法', dataType: 'String', width: 120, dataMap: 'payment'},
            {header: 'FAX', key: 'fax', binding: 'FAX', dataType: 'Boolean', width: 80},
        ],
        'オークション': [
            {header: '日付', key: 'date', binding: '日付', dataType: 'Date', format: 'yyyy/MM/dd', width: 150},
            {header: '参加費', key: 'entry_fee', binding: '参加費', dataType: 'Number', width: 100},
            {header: '参加費2', key: 'entry_fee2', binding: '参加費2', dataType: 'Number', width: 100},
            {header: '消費税', key: 'consumption_tax', binding: '消費税', dataType: 'Number', width: 100},
            {header: 'レポート名', key: 'report_name', binding: 'レポート名', dataMap: 'report', width: 200},
            {header: 'メモ', key: 'memo', binding: 'メモ', width: 300},
        ],
        'カテゴリ': [
            {header: '大分類', key: 'major_category', binding: '大分類', width: 100},
            {header: '中分類', key: 'middle_category', binding: '中分類', width: 100},
            {header: '小分類', key: 'minor_category', binding: '小分類', width: 100},
            {header: '名称', key: 'name', binding: '名称', width: 200},
            {header: 'ソート', key: 'sort', binding: 'ソート', dataType: 'Number', width: 100},
        ],
        '商品': [
            {header: 'カテゴリ', key: 'category', binding: 'カテゴリ', dataMap: 'category', width: 100},
            {header: '商品名', key: 'product_name', binding: '商品名', width: 200},
            {header: 'キーワード', key: 'keywords', binding: 'キーワード', width: 400},
        ],
        'アイテム': [
            {header: 'ID', key: 'id', binding: 'ID', dataType: 'Number', width: 100},
            {header: 'オークション', key: 'auction', binding: 'オークション', dataMap: 'auction', width: 120},
            {header: '売主', key: 'seller', binding: '売主', dataMap: 'supplier', width: 100},
            {header: '売歩', key: 'seller_rate', binding: '売歩', dataType: 'Number', width: 100},
            {header: '単価', key: 'unit_price', binding: '単価', dataType: 'Number', width: 100},
            {header: '個数', key: 'quantity', binding: '個数', dataType: 'Number', width: 100},
            {header: '商品名', key: 'product_name', binding: '商品名', width: 100},
            {header: 'カテゴリ', key: 'category', binding: 'カテゴリ', dataMap: 'category', width: 150},
            {header: '買主', key: 'buyer', binding: '買主', dataMap: 'bidder', width: 100},
            {header: '買歩', key: 'buyer_rate', binding: '買歩', dataType: 'Number', width: 100},
            {header: 'メモ', key: 'memo', binding: 'メモ', width: 300},
        ],
        'レポート': [
            {header: 'レポート名', key: 'report_name', binding: 'name', width: 300},
            {header: '内容', key: 'content', binding: 'content', width: 600},
            {header: 'グループ', key: 'group', binding: 'group', width: 200},
        ],
        'マスタ': [
            {header: 'カテゴリ', key: 'category', binding: 'category', width: 150},
            {header: '値', key: 'value', binding: 'value', width: 200},
            {header: '順番', key: 'sort_order', binding: 'sort', dataType: 'Number', width: 100},
        ],
        '設定': [
            {header: 'ID', key: 'id', binding: 'ID', width: 100},
            {header: '社名', key: 'company_name', binding: '社名', width: 150},
            {header: '郵便番号', key: 'postal_code', binding: '郵便番号', width: 100},
            {header: '住所', key: 'address', binding: '住所', width: 200},
            {header: '電話番号', key: 'phone', binding: '電話番号', width: 120},
            {header: 'ＦＡＸ', key: 'fax_fullwidth', binding: 'ＦＡＸ', width: 120},
        ],
    };

    /** マスタ種別 (CLASS_BY_SHORTNAME のキー、日本語のまま) → 表示名の翻訳キー */
    private static readonly MASTER_LABEL_KEY: { [name: string]: string } = {
        '顧客': 'kobeac.customer', 'オークション': 'kobeac.auction', 'カテゴリ': 'kobeac.category', '商品': 'kobeac.product',
        'アイテム': 'kobeac.item', 'レポート': 'kobeac.report', 'マスタ': 'kobeac.master', '設定': 'kobeac.tab_settings',
    };

    constructor(private dataService: DataService, private kobeac: KobeacService, private route: ActivatedRoute,
                private transloco: TranslocoService) {
    }

    /** マスタ種別名の翻訳キー (未定義ならそのまま表示)。 */
    masterLabel(name: string): string {
        return KobeacMasterComponent.MASTER_LABEL_KEY[name] || name;
    }

    /** カードヘッダ/画面名。単一固定なら「オークション管理」「顧客管理」等、通常は「マスター管理」。 */
    get pageTitle(): string {
        if (this.fixedMaster) {
            return this.transloco.translate('kobeac.master_title_fixed', {name: this.transloco.translate(this.masterLabel(this.fixedMaster))});
        }
        return this.transloco.translate('kobeac.master_management');
    }

    async ngOnInit() {
        // 専用メニュー (admin/auction, admin/customer) では route data.master で1種別に固定する。
        const fixed = this.route.snapshot.data['master'];
        this.fixedMaster = (fixed && KobeacService.CLASS_BY_SHORTNAME[fixed]) ? fixed : null;
        if (this.fixedMaster) {
            this.menus = [this.fixedMaster];
            this.shortname = this.fixedMaster;
        } else {
            this.menus = Object.keys(KobeacService.CLASS_BY_SHORTNAME)
                .filter((m) => KobeacMasterComponent.HIDDEN_IN_MASTER.indexOf(m) < 0);
            this.shortname = this.menus.length ? this.menus[0] : this.shortname;
        }
        await this.kobeac.ensureLoaded();
        this.auctionMap = this.kobeac.buildDataMap(this.kobeac.auctions, (a) => this.kobeac.formatOrientDate(a['日付']));
        this.supplierMap = this.kobeac.buildDataMap(this.kobeac.customers.filter((c) => c['売主']), (c) => c.ID + ' ' + c['名称']);
        this.bidderMap = this.kobeac.buildDataMap(this.kobeac.customers.filter((c) => c['買主']), (c) => c.ID + ' ' + c['名称']);
        this.categoryMap = this.kobeac.buildDataMap(this.kobeac.categories, (c) => c['名称']);
        await this.buildReportMap();
        await this.buildPaymentMap();
        this.selectMaster(this.shortname);
    }

    dataMapFor(name?: string): wjcGrid.DataMap | null {
        switch (name) {
            case 'auction': return this.auctionMap;
            case 'supplier': return this.supplierMap;
            case 'bidder': return this.bidderMap;
            case 'category': return this.categoryMap;
            case 'report': return this.reportMap;
            case 'payment': return this.paymentMap;
            default: return null;
        }
    }

    /**
     * AC_Report の name 一覧から文字列→文字列の DataMap を作る。オークションの
     * 「レポート名」列で使い、AC_Auction.レポート名 には name 文字列がそのまま入る。
     * (buildDataMap は LINK(@rid)→表示 用なので文字列フィールドには使えない。)
     */
    private async buildReportMap(): Promise<void> {
        const names = await new Promise<string[]>((resolve) => {
            this.dataService.query({sql: 'SELECT name FROM AC_Report ORDER BY name', count: 'SELECT count(*) FROM AC_Report'},
                ActivePromotionConfig.SERVER_URL).subscribe(
                (res: any) => {
                    const list: string[] = [];
                    if (wjcCore.isArray(res.results)) {
                        for (const r of res.results) {
                            const n = r && r.name != null ? String(r.name) : '';
                            if (n) list.push(n);
                        }
                    }
                    resolve(list);
                },
                () => resolve([]));
        });
        const rows = names.map((n) => ({name: n}));
        this.reportMap = new wjcGrid.DataMap(rows, 'name', 'name');
    }

    /**
     * AC_Master(category='支払方法') の値から文字列→文字列の DataMap を作る。顧客の
     * 「支払方法」列で使い、AC_Customer.支払方法 には value 文字列がそのまま入る。
     * 値の追加・並び替えはマスター管理のマスタ (category=支払方法) で行う。
     */
    private async buildPaymentMap(): Promise<void> {
        const sql = "SELECT value FROM AC_Master WHERE category = '支払方法' ORDER BY sort";
        const values = await new Promise<string[]>((resolve) => {
            this.dataService.query({sql: sql, count: sql}, ActivePromotionConfig.SERVER_URL).subscribe(
                (res: any) => {
                    const list: string[] = [];
                    if (wjcCore.isArray(res.results)) {
                        for (const r of res.results) {
                            const v = r && r.value != null ? String(r.value) : '';
                            if (v && list.indexOf(v) < 0) list.push(v);
                        }
                    }
                    resolve(list);
                },
                () => resolve([]));
        });
        const rows = values.map((v) => ({value: v}));
        this.paymentMap = new wjcGrid.DataMap(rows, 'value', 'value');
    }

    /**
     * マスタ選択メニューの itemClicked。Wijmo Angular ラッパは itemClicked で
     * $event に EventArgs (selectedValue を持たない) を渡すため、旧実装の
     * $event.selectedValue は常に undefined になり `SELECT FROM undefined` で失敗していた。
     * itemClicked 発火時に確定している menu.selectedIndex を menus[] に対応付けて解決する。
     */
    onMasterMenu(menu: any): void {
        const idx = menu != null && wjcCore.isInt(menu.selectedIndex) ? menu.selectedIndex : -1;
        const name = (idx >= 0 && idx < this.menus.length) ? this.menus[idx] : null;
        if (name && KobeacService.CLASS_BY_SHORTNAME[name]) this.selectMaster(name);
    }

    selectMaster(name: string): void {
        this.shortname = name;
        this.className = KobeacService.CLASS_BY_SHORTNAME[name];
        this.columns = this.COLUMNS[name] || [];
        this.readonly = true;
        this.cv = new OrientDBCollectionView(this.dataService, ActivePromotionConfig.DATABASE,
            'SELECT FROM ' + this.className, this.className, '', 0, 5000, true, true, false);
        this.cv.url = ActivePromotionConfig.SERVER_URL;
        this.cv.tokenName = ActivePromotionConfig.DATABASE + '_token';
        this.cv.fetchPlan = '*:0';
        if (this.className == 'AC_Item') {
            this.cv.itemsSourceFunction = (rows: any[]) => this.kobeac.resolveCustomers(rows);
        }
        // filterOnServer=false で生成して問合せを保留し、既定の並び順を設定してから 1 回だけ取得する
        const sort = KobeacMasterComponent.DEFAULT_SORT[name];
        if (sort) {
            this.cv.sortDescriptions.push(new wjcCore.SortDescription(sort.binding, sort.ascending));
        }
        this.cv.filterOnServer = true;
    }

    isEdited(row: wjcGrid.Row): boolean {
        if (row.dataItem == null) return false;
        for (const item of this.cv.itemsEdited) {
            if (item['@rid'] !== undefined && item['@rid'] == row.dataItem['@rid']) return true;
        }
        return false;
    }

    isNew(row: wjcGrid.Row): boolean {
        return row.dataItem != null && row.dataItem['@rid'] === undefined;
    }

    itemFormatter = (panel: wjcGrid.GridPanel, r: number, c: number, cell: HTMLElement) => {
        if (panel.cellType == wjcGrid.CellType.Cell && panel.rows[r].dataItem != null) {
            // 選択中の見た目は style.scss の .ap-readonly-tint で選択色に戻す (選択変更では itemFormatter が再実行されない)
            if (panel.grid.isReadOnly || panel.rows[r].isReadOnly || panel.columns[c].isReadOnly) {
                // 読み取り専用は背景色だけで示し、文字は通常の黒のまま (代表指示: 灰色は読みにくい)
                cell.style.backgroundColor = '#ffffe0';
            } else {
                cell.style.backgroundColor = '';
            }
        }
    };

    export(): void {
        if (this.grid == null) return;
        const filename = 'export(' + wjcCore.Globalize.formatDate(new Date(), 'yyyyMMddHHmmss') + ').xlsx';
        wjcGridXlsx.FlexGridXlsxConverter.saveAsync(this.grid, {includeColumnHeaders: true, includeCellStyles: false}, filename);
    }

    saveChanges(): void {
        this.cv.saveChanges();
    }

    cancelChanges(): void {
        this.cv.cancelChanges();
    }
}
